import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { quotesApi } from '@/lib/api/client';
import { formatCurrency, formatCreditWithPerShare } from '@/lib/formatters';
import { SpreadStrikes } from '@/components/ui/SpreadStrikes';
import { cn } from '@/lib/utils';
import { CopyOrderButton } from '@/components/CopyOrderButton';
import { SchwabLink } from '@/components/SchwabLink';
import { schwabResearchUrl } from '@/lib/schwabLinks';
import { EventRiskLabel, CalendarCoverageNotice, firstEventRows, eventRowClasses, type EventRisk } from '@/components/EventRiskLabel';

interface TradeSuggestionsProps {
  symbol: string;
  sharesHeld?: number;
  autoLoad?: boolean;
}

export interface TradeSuggestionTableProps {
  suggestions: NonNullable<Awaited<ReturnType<typeof quotesApi.getTradeSuggestions>>>['suggestions'];
  symbol: string;
  quantity: number;
  sharesHeld: number;
  calendarCoverage?: { hasData: boolean; lastConfirmedAt: Date | null };
}

/** Presentational: no hooks, so it renders in a test with plain props. */
export function TradeSuggestionTable({
  suggestions, symbol, quantity, sharesHeld, calendarCoverage,
}: TradeSuggestionTableProps) {
  // The API annotates each side, not the row. PROXIMITY is a property of the
  // expiration, so every side on a row carries the same earnings/ex-div dates --
  // read whichever one exists. `ironCondor.callSide` is the same object as
  // `callSpread`, so it adds nothing.
  //
  // coveredCall must be in this chain, not omitted as "the case where a symbol
  // has no shares". A row can have BOTH spreads null and a covered call present:
  // the spread finders need a strike in the 10-15 delta band, the covered-call
  // finder accepts 20-35, so an expiry with no qualifying spread strikes still
  // yields a covered call whenever shares are held. Skipping it left that row
  // unlabelled AND, because firstEventRows ignores null rows, pushed the single
  // boundary line one row late — the one thing the whole treatment rests on.
  //
  // Order matters for one field only. exDivExceedsExtrinsic does NOT agree
  // across sides: quotes.ts passes a short call for the call side and null for
  // puts, so the put side always reports false. Read the call sides first so an
  // assignment warning cannot be masked by a put that structurally cannot carry
  // one. (It cannot fire on this endpoint today — these calls are strictly OTM —
  // but the ordering should not be the reason.)
  const rowRisks: Array<EventRisk | null> = suggestions.map(s => {
    const side = s.callSpread ?? s.coveredCall ?? s.putSpread ?? null;
    if (!side || side.earningsInWindow === undefined) return null;
    return {
      earningsInWindow: side.earningsInWindow ?? false,
      earningsDate: side.earningsDate ?? null,
      earningsTiming: side.earningsTiming ?? null,
      exDivInWindow: side.exDivInWindow ?? false,
      exDivDate: side.exDivDate ?? null,
      exDivAmount: side.exDivAmount ?? null,
      exDivExceedsExtrinsic: side.exDivExceedsExtrinsic ?? false,
    };
  });
  const eventBoundaries = firstEventRows(rowRisks);

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-3">
        <CalendarCoverageNotice symbol={symbol} coverage={calendarCoverage} />
        <SchwabLink
          href={schwabResearchUrl(symbol)}
          label={`${symbol} on Schwab`}
          ariaLabel={`Research ${symbol} on Schwab`}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 border-b border-gray-200">
              <th className="text-left py-2 px-3 font-medium">Expiration</th>
              <th className="text-center py-2 px-3 font-medium">DTE</th>
              <th className="text-center py-2 px-3 font-medium">Put Spread</th>
              <th className="text-right py-2 px-3 font-medium">Credit</th>
              <th className="text-center py-2 px-3 font-medium">Call Spread</th>
              <th className="text-right py-2 px-3 font-medium">Credit</th>
              <th className="text-right py-2 px-3 font-medium">IC Credit</th>
              <th className="text-right py-2 px-3 font-medium">Copy</th>
              {sharesHeld >= 100 && (
                <>
                  <th className="text-center py-2 px-3 font-medium border-l border-gray-200">Covered Call</th>
                  <th className="text-right py-2 px-3 font-medium">Premium</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {suggestions.map((s, i) => {
              const risk = rowRisks[i];
              const isEarningsBoundary = eventBoundaries.earnings === i;
              const isExDivBoundary = eventBoundaries.exDiv === i;
              const isEventBoundary = isEarningsBoundary || isExDivBoundary;
              return (
                <tr key={s.expiration} className={[
                  'border-b border-gray-50 hover:bg-gray-50',
                  eventRowClasses({ risk, isEarningsBoundary, isExDivBoundary }),
                ].filter(Boolean).join(' ')}>
                <td className="py-2.5 px-3 font-mono text-gray-900">
                  <div>{s.expiration}</div>
                  <EventRiskLabel risk={risk} emphasis={isEventBoundary} />
                </td>
                <td className="py-2.5 px-3 text-center">
                  <span className={cn('text-sm', s.dte <= 7 ? 'text-yellow-600 font-medium' : 'text-gray-600')}>
                    {s.dte}d
                  </span>
                </td>

                {/* Put Spread */}
                <td className="py-2.5 px-3 text-center">
                  {s.putSpread ? (
                    <SpreadStrikes
                      shortStrike={s.putSpread.shortStrike}
                      longStrike={s.putSpread.longStrike}
                      optionType="put"
                      shortDelta={s.putSpread.shortDelta}
                    />
                  ) : <span className="text-gray-300">-</span>}
                </td>
                <td className="py-2.5 px-3 text-right">
                  {s.putSpread ? (
                    <span className="font-mono text-xs text-green-600">{formatCreditWithPerShare(s.putSpread.credit, quantity)}</span>
                  ) : '-'}
                </td>

                {/* Call Spread */}
                <td className="py-2.5 px-3 text-center">
                  {s.callSpread ? (
                    <SpreadStrikes
                      shortStrike={s.callSpread.shortStrike}
                      longStrike={s.callSpread.longStrike}
                      optionType="call"
                      shortDelta={s.callSpread.shortDelta}
                    />
                  ) : <span className="text-gray-300">-</span>}
                </td>
                <td className="py-2.5 px-3 text-right">
                  {s.callSpread ? (
                    <span className="font-mono text-xs text-green-600">{formatCreditWithPerShare(s.callSpread.credit, quantity)}</span>
                  ) : '-'}
                </td>

                {/* Iron Condor total */}
                <td className="py-2.5 px-3 text-right">
                  {s.ironCondor ? (
                    <span className="font-mono text-sm font-medium text-green-600">
                      {formatCreditWithPerShare(s.ironCondor.totalCredit, quantity)}
                    </span>
                  ) : '-'}
                </td>

                {/* Copy buttons */}
                <td className="py-2.5 px-3 text-right">
                  <div className="flex gap-2 justify-end">
                    {s.putSpread && (
                      <CopyOrderButton
                        order={{
                          symbol,
                          strategy: 'Vertical Put Spread',
                          legs: [
                            { action: 'STO', quantity, symbol, expiration: s.expiration, strike: s.putSpread.shortStrike, optionType: 'put' },
                            { action: 'BTO', quantity, symbol, expiration: s.expiration, strike: s.putSpread.longStrike, optionType: 'put' },
                          ],
                          netCredit: (s.putSpread.credit / quantity) / 100,
                        }}
                      />
                    )}
                    {s.callSpread && (
                      <CopyOrderButton
                        order={{
                          symbol,
                          strategy: 'Vertical Call Spread',
                          legs: [
                            { action: 'STO', quantity, symbol, expiration: s.expiration, strike: s.callSpread.shortStrike, optionType: 'call' },
                            { action: 'BTO', quantity, symbol, expiration: s.expiration, strike: s.callSpread.longStrike, optionType: 'call' },
                          ],
                          netCredit: (s.callSpread.credit / quantity) / 100,
                        }}
                      />
                    )}
                    {s.ironCondor && (
                      <CopyOrderButton
                        order={{
                          symbol,
                          strategy: 'Iron Condor',
                          legs: [
                            { action: 'STO', quantity, symbol, expiration: s.expiration, strike: s.ironCondor.putSide.shortStrike, optionType: 'put' },
                            { action: 'BTO', quantity, symbol, expiration: s.expiration, strike: s.ironCondor.putSide.longStrike, optionType: 'put' },
                            { action: 'STO', quantity, symbol, expiration: s.expiration, strike: s.ironCondor.callSide.shortStrike, optionType: 'call' },
                            { action: 'BTO', quantity, symbol, expiration: s.expiration, strike: s.ironCondor.callSide.longStrike, optionType: 'call' },
                          ],
                          netCredit: (s.ironCondor.totalCredit / quantity) / 100,
                        }}
                      />
                    )}
                  </div>
                </td>

                {/* Covered Call */}
                {sharesHeld >= 100 && (
                  <>
                    <td className="py-2.5 px-3 text-center border-l border-gray-100">
                      {s.coveredCall ? (
                        <span className="font-mono text-xs">
                          <span className="text-blue-600">${s.coveredCall.strike}</span>
                          <span className="text-gray-400 ml-1">({s.coveredCall.delta}δ)</span>
                          <span className="text-gray-400 ml-1">×{s.coveredCall.contracts}</span>
                        </span>
                      ) : '-'}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      {s.coveredCall ? (
                        <span className="font-mono text-xs text-green-600">
                          {formatCurrency(s.coveredCall.premium)}
                        </span>
                      ) : '-'}
                    </td>
                  </>
                )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function TradeSuggestions({ symbol, sharesHeld = 0, autoLoad = false }: TradeSuggestionsProps) {
  const [enabled, setEnabled] = useState(autoLoad);
  const [spreadWidth, setSpreadWidth] = useState(5);

  const { data, isLoading, error } = useQuery({
    queryKey: ['trade-suggestions', symbol, spreadWidth, sharesHeld],
    queryFn: () => quotesApi.getTradeSuggestions({
      underlyingSymbol: symbol,
      spreadWidth,
      sharesHeld,
    }),
    enabled,
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
  });

  if (!enabled) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-gray-500 mb-3">
            Get suggested trades based on your 10-15 delta targets
          </p>
          <Button variant="primary" size="sm" onClick={() => setEnabled(true)}>
            Load Suggestions for {symbol}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Spinner size="sm" />
          <p className="text-sm text-gray-500 mt-2">Loading option chain...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    const msg = error instanceof Error ? error.message : 'Failed to load';
    const isSchwabError = msg.includes('Schwab') || msg.includes('token') || msg.includes('401');
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-red-600">
            {isSchwabError
              ? 'Schwab connection required for live option data. Reconnect in Settings.'
              : msg}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.suggestions.length === 0) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-gray-500">No suggestions available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500">Spread width:</span>
        {[2, 5, 10].map(w => (
          <Button
            key={w}
            variant={spreadWidth === w ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setSpreadWidth(w)}
          >
            ${w}
          </Button>
        ))}
        <span className="text-xs text-gray-400 ml-2">
          {symbol} @ ${data.underlyingPrice.toFixed(2)} · {data.deltaRange.min}-{data.deltaRange.max} delta
        </span>
      </div>

      {/* Suggestions table */}
      <Card>
        <CardContent className="p-0">
          <TradeSuggestionTable
            suggestions={data.suggestions}
            symbol={symbol}
            quantity={data.quantity}
            sharesHeld={sharesHeld}
            calendarCoverage={data.calendarCoverage}
          />
        </CardContent>
      </Card>

      {/* IV note */}
      {data.suggestions[0]?.putSpread?.shortIV && (
        <p className="text-xs text-gray-400">
          IV: Put {data.suggestions[0].putSpread.shortIV.toFixed(1)}%
          {data.suggestions[0].callSpread?.shortIV && ` · Call ${data.suggestions[0].callSpread.shortIV.toFixed(1)}%`}
        </p>
      )}
    </div>
  );
}
